import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../context/LanguageContext";
import { api, bookUrl, stripNikud } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { formatDateForLang } from "../lib/dateFormat";
import {
  Calendar,
  Sparkles,
  ArrowLeft,
  Flame,
  BookOpen,
} from "lucide-react";
import { Button } from "../components/ui/button";
import AIAssistant from "../components/AIAssistant";
import ShareCard from "../components/share/ShareCard";
import ShareMenu from "../components/share/ShareMenu";
import SaveToNotebookButton from "../components/SaveToNotebookButton";
import { formatHebrewDate, formatHebrewDateEn, hebrewDayLetters } from "../lib/hebrewGematria";
import { numberToHebrew } from "../lib/hebrewRef";
import { toast } from "sonner";

/**
 * Daily verse & daily learning page.
 *
 * Shows today's rotating pasuk with full context:
 *   - Large parchment card with the verse in Hebrew + English
 *   - Book/chapter/verse citation with link to the chapter reader
 *   - Streak tracker (localStorage)
 *   - AI insight panel (commentary summary via GPT-5.2)
 *   - Share as text / as image
 */
export default function Daily() {
  const { t } = useTranslation();
  const { isEn } = useLanguage();
  const { user } = useAuth();
  const { nikud } = useTheme();
  const [daily, setDaily] = useState(null);
  const [loading, setLoading] = useState(true);
  const [streak, setStreak] = useState({ current: 0, last: null });
  const [showAI, setShowAI] = useState(false);
  const shareRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    api
      .dailyVerse()
      .then(setDaily)
      .catch(() => toast.error(t("daily_extra.load_error")))
      .finally(() => setLoading(false));

    // Streak logic — increment once per day when user opens this page
    try {
      const raw = localStorage.getItem("tanakh_daily_streak");
      const today = new Date().toDateString();
      if (!raw) {
        const next = { current: 1, last: today };
        localStorage.setItem("tanakh_daily_streak", JSON.stringify(next));
        setStreak(next);
      } else {
        const parsed = JSON.parse(raw);
        if (parsed.last === today) {
          setStreak(parsed);
        } else {
          const y = new Date();
          y.setDate(y.getDate() - 1);
          const yesterday = y.toDateString();
          const next =
            parsed.last === yesterday
              ? { current: parsed.current + 1, last: today }
              : { current: 1, last: today };
          localStorage.setItem("tanakh_daily_streak", JSON.stringify(next));
          setStreak(next);
        }
      }
    } catch {
      /* noop */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dailyShareText = useCallback(() => {
    if (!daily) return "";
    return `${stripNikud(daily.he || "")}\n— ${daily.book_he} ${numberToHebrew(daily.chapter)}:${numberToHebrew(daily.verse)}`;
  }, [daily]);

  // Hebrew date is the primary line. In Hebrew mode it uses gematria
  // letters per the printed siddur / chumash convention ("ד׳ בסיון
  // תשפ״ו"). In English mode it switches to a Standard Hebrew
  // transliteration ("4 Sivan 5786") — readable for the worldwide
  // Jewish learner without sacrificing the Hebrew-calendar anchor.
  const now = new Date();
  const hebrewDateLetters = isEn ? formatHebrewDateEn(now) : formatHebrewDate(now);
  // Gregorian date: Hebrew locale (DD/MM/YYYY-style long form) by
  // default, switching to American long form (e.g. "Thursday, June 11,
  // 2026") when the UI is in English. The Hebrew formatting is byte-
  // identical to the prior behaviour.
  const gregorianStr = formatDateForLang(now, isEn ? "en" : "he", { dateStyle: "long_weekday" });

  return (
    <div className="relative min-h-[calc(100vh-4rem)]" data-testid="daily-page">
      <section className="mx-auto max-w-4xl px-4 sm:px-6 pt-10 md:pt-16 pb-24">
        {/* Header */}
        <div className="text-center space-y-3 mb-10">
          <div className="inline-flex items-center gap-2 text-gold">
            <Calendar className="h-4 w-4" strokeWidth={1.5} />
            <span className="font-latin italic text-xs tracking-[0.35em] uppercase">
              {t("daily_extra.eyebrow")}
            </span>
          </div>
          <h1 className="font-heading text-5xl md:text-6xl text-foreground">
            {t("daily_extra.page_title")}
          </h1>
          <p
            className="font-heading text-base md:text-lg text-foreground"
            data-testid="daily-hebrew-date"
          >
            {hebrewDateLetters}
          </p>
          <p
            className="text-xs text-muted-foreground/80"
            data-testid="daily-gregorian-date"
          >
            {gregorianStr}
          </p>
          <div className="ornamental-divider mx-auto max-w-xs">✦ ❖ ✦</div>
        </div>

        {/* Streak pill */}
        {streak.current > 0 && (
          <div
            className="mx-auto mb-8 inline-flex items-center gap-3 rounded-full border border-gold/40 bg-gold/5 px-5 py-2.5"
            data-testid="daily-streak-pill"
            style={{ display: "flex", justifyContent: "center", width: "fit-content", marginLeft: "auto", marginRight: "auto" }}
          >
            <Flame className="h-4 w-4 text-gold" strokeWidth={1.5} />
            <span className="font-heading text-sm text-foreground">
              {t("daily_extra.streak_pill", {
                n: streak.current,
                unit: streak.current === 1 ? t("daily_extra.day_singular") : t("daily_extra.day_plural"),
              })}
            </span>
          </div>
        )}

        {/* Verse card */}
        {loading ? (
          <div className="space-y-4">
            <div className="h-10 shimmer-bg rounded" />
            <div className="h-10 shimmer-bg rounded w-5/6 mx-auto" />
            <div className="h-10 shimmer-bg rounded w-4/6 mx-auto" />
          </div>
        ) : daily ? (
          <article
            data-testid="daily-verse-article"
            className="relative rounded-sm border border-gold/40 bg-card shadow-book p-8 md:p-14 overflow-hidden"
          >
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-l from-transparent via-gold/70 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-l from-transparent via-gold/60 to-transparent" />

            <p
              dir="rtl"
              data-testid="daily-verse-text"
              className="torah-verse hebrew-text text-3xl md:text-5xl text-foreground leading-loose text-center"
            >
              {nikud ? daily.he : stripNikud(daily.he || "")}
            </p>

            {daily.en && (
              <>
                <div className="ornamental-divider mt-10 mx-auto max-w-xs">✦</div>
                <p
                  dir="ltr"
                  lang="en"
                  className="font-latin italic text-base md:text-lg text-muted-foreground text-center leading-relaxed mt-8"
                >
                  &ldquo;{daily.en}&rdquo;
                </p>
              </>
            )}

            <div className="mt-10 flex items-center justify-center gap-3">
              <Link
                to={`/reader/${bookUrl(daily.book)}/${daily.chapter}?v=${daily.verse}`}
                data-testid="daily-citation-link"
                className="font-heading text-lg md:text-xl text-gold hover:underline underline-offset-4 decoration-gold/40 transition-colors inline-flex items-center min-h-[44px] px-2"
              >
                {isEn
                  ? `${daily.book} · ${t("daily_extra.chapter_word")} ${daily.chapter} · ${t("daily_extra.verse_word")} ${daily.verse}`
                  : `${daily.book_he} · פרק ${hebrewDayLetters(daily.chapter)} · פסוק ${hebrewDayLetters(daily.verse)}`}
              </Link>
            </div>

            {/* Actions */}
            <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
              <Link
                to={`/reader/${bookUrl(daily.book)}/${daily.chapter}?v=${daily.verse}`}
                data-testid="daily-open-verse"
                className="sacred-btn-gold inline-flex items-center gap-2 px-5 py-2 rounded-sm"
              >
                <BookOpen className="h-4 w-4" strokeWidth={1.5} />
                {t("daily_extra.open_verse")}
                <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
              </Link>
              <Button
                variant="ghost"
                onClick={() => setShowAI((s) => !s)}
                data-testid="daily-ai-btn"
                className="sacred-btn-ghost"
              >
                <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                {showAI ? t("daily_extra.close_explanation") : t("daily_extra.explain_smart")}
              </Button>
              <ShareMenu
                getImageNode={() => shareRef.current}
                getText={dailyShareText}
                filename={`torahya-${daily.book}-${daily.chapter}-${daily.verse}`}
                title={`${daily.book_he} ${numberToHebrew(daily.chapter)}:${numberToHebrew(daily.verse)}`}
                buttonClassName="sacred-btn-ghost"
                testIdSuffix="daily"
                align="end"
              />
              {daily?.he && (
                <SaveToNotebookButton
                  variant="chip"
                  testIdSuffix="daily"
                  className="sacred-btn-ghost"
                  payload={{
                    source_type: "daily",
                    source_book: daily.book,
                    source_chapter: daily.chapter,
                    source_verse: daily.verse,
                    source_ref_he: `${daily.book_he || daily.book} ${numberToHebrew(daily.chapter)}:${numberToHebrew(daily.verse)}`,
                    content_he: daily.he || "",
                    content_text: daily.en || "",
                  }}
                />
              )}
            </div>
          </article>
        ) : (
          <div className="text-center text-muted-foreground py-16">
            <p>{t("daily_extra.no_daily")}</p>
          </div>
        )}

        {/* AI insight */}
        {showAI && daily && (
          <div
            className="mt-10 rounded-sm border border-gold/25 bg-card/80 p-6 md:p-8 animate-fade-in-up"
            data-testid="daily-ai-panel"
          >
            <div className="flex items-center gap-2 text-gold mb-4">
              <Sparkles className="h-4 w-4" strokeWidth={1.5} />
              <span className="font-heading text-sm tracking-widest uppercase">
                {t("daily_extra.smart_commentary")}
              </span>
            </div>
            <AIAssistant
              book={daily.book}
              chapter={daily.chapter}
              verse={daily.verse}
              enabled={!!user}
            />
          </div>
        )}

        {/* Hidden share card target — premium parchment-on-navy */}
        {daily && (
          <div
            aria-hidden
            style={{
              position: "fixed",
              left: -99999,
              top: 0,
              pointerEvents: "none",
              opacity: 0,
            }}
          >
            <ShareCard
              ref={shareRef}
              size="square"
              lang={isEn ? "en" : "he"}
              eyebrow={isEn
                ? `Daily Verse · ${gregorianStr}`
                : `פסוק היום · ${hebrewDateLetters}`}
              title={isEn
                ? `${daily.book} ${daily.chapter}:${daily.verse}`
                : `${daily.book_he} ${numberToHebrew(daily.chapter)}:${numberToHebrew(daily.verse)}`}
              body={isEn
                ? (daily.en || (nikud ? daily.he : stripNikud(daily.he || "")))
                : (nikud ? daily.he : stripNikud(daily.he || ""))}
              secondary={isEn ? null : daily.en}
              citation={isEn
                ? `${daily.book} · Chapter ${daily.chapter} · Verse ${daily.verse}`
                : `${daily.book_he} · פרק ${numberToHebrew(daily.chapter)} · פסוק ${numberToHebrew(daily.verse)}`}
            />
          </div>
        )}
      </section>
    </div>
  );
}
